import React from 'react';
import { BookOpen, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

import useAlertStore from '@/store/alert.store';
import type { AgentKnowledgeBase } from '@/types/agent.type';

type KnowledgeBaseRowProps = {
  knowledgeBase: AgentKnowledgeBase;
  onUnlink: (kbId: number) => void;
  isUnlinking: boolean;
};

export const KnowledgeBaseRow = React.memo(function KnowledgeBaseRow({
  knowledgeBase,
  onUnlink,
  isUnlinking,
}: KnowledgeBaseRowProps) {
  const handleUnlink = (e: React.MouseEvent) => {
    e.stopPropagation();
    useAlertStore.getState().openAlert({
      title: 'Unlink knowledge base',
      description: `Are you sure you want to unlink "${knowledgeBase.name}" from this agent? The knowledge base will not be deleted.`,
      confirmText: 'Unlink',
      onConfirm: () => onUnlink(knowledgeBase.id),
      type: 'destructive',
    });
  };

  return (
    <div className="border rounded-lg">
      <div className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-3 flex-1">
          <BookOpen className="h-5 w-5 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{knowledgeBase.name}</div>
            {knowledgeBase.kb_description && (
              <div className="text-xs text-muted-foreground truncate">
                {knowledgeBase.kb_description}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20"
            onClick={handleUnlink}
            disabled={isUnlinking}
            title="Unlink knowledge base"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
});
